import { SquareArrowOutUpRightIcon } from 'lucide-react'
import { useCallback } from 'react'

import { AssessmentInfo } from '@/gen/inventory/v1/assessment_service_pb'
import type { Company } from '@/gen/inventory/v1/company_service_pb'

import { useTrackCallback } from '@/lib/analytics/events'
import { cn } from '@/lib/ui/style-helpers'
import { buildQuestionnairePortalUrl } from '@/lib/url/urls'

import { RecipientsButton } from '@/pages/assessment/assessment-tabs/smart-evidence-request/recipients-button'
import { AssessmentDueDatePicker } from '@/pages/assessment/components/assessment-due-date-picker'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'

import { SendReminderButton } from './send-reminder-button'
import { useQuestionnaireInviteRecipient } from './use-questionnaire-invite-recipient'

type QuestionnairesHeaderProps = {
  company: Company
  readonly: boolean
  dueDate: AssessmentInfo['dueDate']
  assessmentId: AssessmentInfo['id']
  className?: string
  hasQuestionnaires: boolean
}

export const QuestionnairesHeader = ({
  company,
  readonly,
  dueDate,
  assessmentId,
  className,
  hasQuestionnaires,
}: QuestionnairesHeaderProps) => {
  const companyId = company.id
  const companyName = company.profile?.name ?? ''
  const recipientsEmails = company.recipientsEmails
  const { InviteRecipientModal, openInviteRecipientModal } = useQuestionnaireInviteRecipient({
    company,
  })

  const trackThirdPartyPortalCallback = useTrackCallback(
    'third-party.smart-evidence-request.go-to-third-party-portal',
  )
  const trackQuestionnaireInviteClick = useTrackCallback('questionnaire.invite.click')
  const trackDueDateClick = useTrackCallback('third-party.smart-evidence-request.due-date.click')
  const trackDueDateSet = useTrackCallback('third-party.smart-evidence-request.due-date.set')

  const handleThirdPartyPortalClick = useCallback(() => {
    trackThirdPartyPortalCallback({
      assessmentId,
      companyId,
      companyName,
      thirdPartyId: companyId,
    })
    window.open(buildQuestionnairePortalUrl(companyId), '_blank')
  }, [trackThirdPartyPortalCallback, assessmentId, companyId, companyName])

  return (
    <>
      <div
        className={cn(
          'mb-6 flex min-h-13 flex-wrap items-center justify-between gap-x-4 gap-y-3 rounded bg-main-background-muted px-4 py-2',
          className,
        )}
      >
        <div className='flex flex-wrap items-center gap-x-2 gap-y-2'>
          <RecipientsButton
            recipientsEmails={recipientsEmails}
            onClick={() => {
              trackQuestionnaireInviteClick({
                companyId,
                assessmentId,
              })
              openInviteRecipientModal()
            }}
            readonly={readonly}
          />

          {(!readonly || dueDate) && (
            <>
              <Separator orientation='vertical' className='mr-2 h-4' />
              <AssessmentDueDatePicker
                assessmentId={assessmentId}
                dueDate={dueDate}
                readonly={readonly}
                onDatePickerOpen={() => trackDueDateClick({ assessmentId, companyId })}
                onDateSelected={(date) =>
                  trackDueDateSet({ assessmentId, companyId, dueDate: date.toISOString() })
                }
              />
            </>
          )}

          {!readonly && hasQuestionnaires && <SendReminderButton company={company} />}
        </div>

        <div className='flex items-center gap-2'>
          <Button variant='link' onClick={handleThirdPartyPortalClick}>
            <TextWithIcon
              text='Third-party portal'
              icon={<SquareArrowOutUpRightIcon className='size-3.5' />}
            />
          </Button>
        </div>
      </div>

      {InviteRecipientModal()}
    </>
  )
}
